import React from 'react'
import { Button, Card, Empty, Skeleton, Tag } from 'antd'
import type { ContinueReadingItem } from '../types/home'
import HomeBookCard from './home/HomeBookCard'

type ContinueReadingSectionProps = {
  items: ContinueReadingItem[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  usingFallback: boolean
  onLoadMore: () => void
  onBookClick: (item: ContinueReadingItem, position: number) => void
  onDiscoverMore: () => void
}

const formatProgressText = (value: number) => `已读 ${Math.max(0, Math.min(100, Number(value || 0))).toFixed(0)}%`
const formatRemainingText = (value: number) => value > 0 ? `预计剩余 ${value} 分钟` : '可立即开始阅读'
const CARD_WIDTH_CLASS = 'w-[calc(50%-0.375rem)] min-w-[160px] max-w-[180px] shrink-0 sm:w-[180px] sm:min-w-[180px] md:w-[190px] md:min-w-[190px] lg:w-[200px] lg:min-w-[200px] xl:w-[170px] xl:min-w-[170px]'

/**
 * 首页“延续阅读”展示区。
 */
const ContinueReadingSection: React.FC<ContinueReadingSectionProps> = ({
  items,
  loading,
  loadingMore,
  hasMore,
  usingFallback,
  onLoadMore,
  onBookClick,
  onDiscoverMore,
}) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const loadingRef = React.useRef(false)

  React.useEffect(() => {
    loadingRef.current = loadingMore
  }, [loadingMore])

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current
    if (!el || loadingRef.current || !hasMore) return
    const remain = el.scrollWidth - el.clientWidth - el.scrollLeft
    if (remain <= 160) {
      onLoadMore()
    }
  }, [hasMore, onLoadMore])

  const content = React.useMemo(() => {
    if (loading && items.length === 0) {
      return (
        <div className="flex gap-3 md:gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className={`${CARD_WIDTH_CLASS} rounded-2xl border border-gray-100 bg-white p-3 md:p-4`}>
              <Skeleton.Image active style={{ width: '100%', height: 220, borderRadius: 16 }} />
              <Skeleton active paragraph={{ rows: 3 }} title={{ width: '90%' }} className="mt-4" />
            </div>
          ))}
        </div>
      )
    }
    if (items.length === 0) {
      return (
        <div className="rounded-2xl bg-gray-50 py-10">
          <Empty
            description="暂时还没有可展示的延续阅读书籍"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={onDiscoverMore}>
              去发现更多好书
            </Button>
          </Empty>
        </div>
      )
    }
    return (
      <div
        ref={scrollRef}
        className="overflow-x-auto pb-2"
        onScroll={handleScroll}
      >
        <div className="flex gap-3 md:gap-4 min-w-full">
          {items.map((item, index) => (
            <div
              key={`${item.book_id}-${index}`}
              className={CARD_WIDTH_CLASS}
            >
              <HomeBookCard
                id={Number(item.book_id)}
                title={item.title}
                author={item.author || '未知作者'}
                cover={item.cover}
                readProgressPercent={item.read_progress_percent}
                description={item.reason}
                topLeftSlot={<Tag color="processing" style={{ marginInlineEnd: 0 }}>{formatProgressText(item.read_progress_percent)}</Tag>}
                metaSlot={(
                  <>
                    <div className="text-xs text-primary-600">{formatRemainingText(item.remaining_minutes)}</div>
                    <div className="mt-1 text-[11px] text-orange-500">{item.hot_tag || '全站热门推荐'}</div>
                  </>
                )}
                onClick={() => onBookClick(item, index)}
              />
            </div>
          ))}
          {loadingMore ? (
            <div className={`${CARD_WIDTH_CLASS} rounded-2xl border border-dashed border-primary-200 bg-primary-50/40 p-4 flex items-center justify-center text-sm text-primary-600`}>
              正在加载更多...
            </div>
          ) : null}
        </div>
      </div>
    )
  }, [handleScroll, items, loading, loadingMore, onBookClick, onDiscoverMore])

  return (
    <Card
      title={<span className="text-lg font-bold text-gray-800">延续阅读</span>}
      extra={(
        <div className="flex items-center gap-2">
          {usingFallback ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>热度兜底</Tag> : null}
          <Button size="small" onClick={hasMore ? onLoadMore : onDiscoverMore} loading={loadingMore}>
            {hasMore ? '更多' : '去发现'}
          </Button>
        </div>
      )}
      className="theme-card home-section-card home-section-card--continue border-none mb-4 md:mb-6"
      styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' } }}
    >
      {content}
    </Card>
  )
}

export default ContinueReadingSection
